/**
 * Klient pro /api/admin/* endpointy.
 * Všechny funkce vyhazují ApiError při !ok — volající si ošetří v try/catch.
 */
import { api } from "./api";

// ─── Firemní profil ───────────────────────────────────────

export type CompanyProfile = {
  id: string;
  name: string;
  legalForm: string | null;
  ico: string | null;
  dic: string | null;
  isVatPayer: boolean;
  street: string;
  city: string;
  zip: string;
  country: string;
  email: string;
  phone: string | null;
  website: string | null;
  bankAccount: string | null;
  iban: string | null;
  bankName: string | null;
  logoUrl: string | null;
  invoiceNote: string | null;
};

export async function getCompanyProfile() {
  const res = await api<{ profile: CompanyProfile }>("/api/admin/settings/company");
  return res.profile;
}

export async function updateCompanyProfile(data: Partial<CompanyProfile>) {
  const res = await api<{ profile: CompanyProfile }>("/api/admin/settings/company", {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return res.profile;
}

export async function uploadCompanyLogo(file: File): Promise<CompanyProfile> {
  const form = new FormData();
  form.append("logo", file);
  const token = localStorage.getItem("admin_token");
  const res = await fetch("/api/admin/settings/logo", {
    method: "POST",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((payload as any)?.message ?? `Upload selhal (${res.status})`);
  return (payload as any).profile as CompanyProfile;
}

export async function deleteCompanyLogo(): Promise<CompanyProfile> {
  const res = await api<{ profile: CompanyProfile }>("/api/admin/settings/logo", {
    method: "DELETE",
  });
  return res.profile;
}

// ─── Ceník dopravy ────────────────────────────────────────

export type TravelPricing = {
  pricePerKmCzk: number;
  maxDistanceKm: number;
};

export async function getTravelPricing() {
  const res = await api<{ pricing: TravelPricing }>("/api/admin/settings/travel-pricing");
  return res.pricing;
}

export async function updateTravelPricing(data: TravelPricing) {
  const res = await api<{ pricing: TravelPricing }>("/api/admin/settings/travel-pricing", {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return res.pricing;
}

// ─── E-mailové schránky (MS Graph) ────────────────────────

export type EmailAccount = {
  id: string;
  mailbox: string;
  displayName: string;
  isDefault: boolean;
  active: boolean;
  createdAt: string;
};

export async function listEmailAccounts() {
  const res = await api<{ accounts: EmailAccount[] }>("/api/admin/email-accounts");
  return res.accounts;
}

export async function createEmailAccount(data: {
  mailbox: string;
  displayName: string;
  isDefault?: boolean;
  active?: boolean;
}) {
  const res = await api<{ account: EmailAccount }>("/api/admin/email-accounts", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.account;
}

export async function deleteEmailAccount(id: string) {
  await api(`/api/admin/email-accounts/${id}`, { method: "DELETE" });
}

// ─── Programy ─────────────────────────────────────────────

export type Program = {
  id: string;
  name: string;
  description: string | null;
  ageGroupFrom: number | null;
  ageGroupTo: number | null;
  durationMinutes: number;
  maxChildrenPerSlot: number;
  setupMinutes: number;
  teardownMinutes: number;
  pricePerChildCzk: number;
  minPriceCzk: number;
  coverImageUrl: string | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  _count?: { reservationItems: number };
  images?: { id: string; url: string; alt: string | null; sortOrder: number }[];
};

export type ProgramInput = Omit<
  Program,
  "id" | "createdAt" | "updatedAt" | "_count" | "sortOrder"
> & { sortOrder?: number };

export async function listProgramsAdmin() {
  const res = await api<{ programs: Program[] }>("/api/admin/programs");
  return res.programs;
}

export async function getProgramAdmin(id: string) {
  const res = await api<{ program: Program }>(`/api/admin/programs/${id}`);
  return res.program;
}

export async function createProgram(data: ProgramInput) {
  const res = await api<{ program: Program }>("/api/admin/programs", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.program;
}

export async function updateProgram(id: string, data: Partial<ProgramInput>) {
  const res = await api<{ program: Program }>(`/api/admin/programs/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return res.program;
}

export async function deleteProgram(id: string) {
  return api<{ ok: boolean; mode: "deleted" | "deactivated"; reservations?: number }>(
    `/api/admin/programs/${id}`,
    { method: "DELETE" }
  );
}

// ─── Sloty (kalendář) ─────────────────────────────────────

export type SlotStatus = "AVAILABLE" | "PENDING" | "RESERVED" | "BLOCKED";

export type AdminSlot = {
  id: string;
  date: string; // ISO datetime
  startTime: string; // "HH:MM"
  endTime: string;
  status: SlotStatus;
  notes: string | null;
  reservationId: string | null;
  reservation: {
    id: string;
    snapshotName: string;
    contactName: string;
    childrenCount: number;
    status: string;
  } | null;
};

export async function listSlots(from: string, to: string) {
  const res = await api<{ slots: AdminSlot[] }>(
    `/api/admin/slots?from=${from}&to=${to}`
  );
  return res.slots;
}

export async function createSlot(data: {
  date: string;
  startTime: string;
  status?: "AVAILABLE" | "BLOCKED";
  notes?: string;
}) {
  const res = await api<{ slot: AdminSlot }>("/api/admin/slots", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.slot;
}

export async function createSlotsBatch(data: {
  dateFrom: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
  daysOfWeek: number[];
}) {
  return api<{ created: number; skipped: number }>("/api/admin/slots/batch", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateSlot(
  id: string,
  data: { status?: "AVAILABLE" | "BLOCKED"; notes?: string | null }
) {
  const res = await api<{ slot: AdminSlot }>(`/api/admin/slots/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return res.slot;
}

export async function deleteSlot(id: string) {
  await api(`/api/admin/slots/${id}`, { method: "DELETE" });
}

// ─── Rezervace ────────────────────────────────────────────

export type ReservationStatus = "PENDING" | "CONFIRMED" | "COMPLETED" | "CANCELLED";

export type ReservationListItem = {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  status: ReservationStatus;
  snapshotName: string;
  snapshotCity: string;
  contactName: string;
  contactEmail: string;
  childrenCount: number;
  distanceKm: number | null;
  totalPriceCzk: number;
  createdAt: string;
  _count?: { programs: number; slots: number };
};

export type ReservationProgramDetail = {
  id: string;
  programId: string;
  childrenCount: number;
  blocksNeeded: number;
  snapshotPricePerChildCzk: number;
  snapshotMinPriceCzk: number;
  snapshotDurationMinutes: number;
  snapshotMaxPerSlot: number;
  computedPriceCzk: number;
  program: { id: string; name: string; coverImageUrl: string | null };
};

export type ReservationDetail = ReservationListItem & {
  endTime: string;
  totalMinutes: number;
  snapshotStreet: string;
  snapshotZip: string;
  snapshotCountry: string;
  snapshotLat: number | null;
  snapshotLon: number | null;
  contactPhone: string | null;
  note: string | null;
  programsPriceCzk: number;
  travelPriceCzk: number;
  durationMin: number | null;
  cancelReason: string | null;
  confirmedAt: string | null;
  completedAt: string | null;
  paymentMethod: "BANK_TRANSFER" | "CASH";
  kindergartenId: string | null;
  programs: ReservationProgramDetail[];
  slots: { id: string; startTime: string; endTime: string; status: string }[];
  invoice: InvoiceOnReservation | null;
};

export async function listReservations(filters?: {
  status?: ReservationStatus | "ALL";
  from?: string;
  to?: string;
}) {
  const params = new URLSearchParams();
  if (filters?.status && filters.status !== "ALL") params.set("status", filters.status);
  if (filters?.from) params.set("from", filters.from);
  if (filters?.to) params.set("to", filters.to);
  const q = params.toString();
  const res = await api<{
    reservations: ReservationListItem[];
    counts: Record<string, number>;
  }>(`/api/admin/reservations${q ? `?${q}` : ""}`);
  return res;
}

export async function getReservation(id: string) {
  const res = await api<{ reservation: ReservationDetail }>(
    `/api/admin/reservations/${id}`
  );
  return res.reservation;
}

export async function confirmReservation(id: string) {
  return api<{
    reservation: ReservationDetail;
    emailSent: boolean;
    emailError: string | null;
  }>(`/api/admin/reservations/${id}/confirm`, { method: "POST" });
}

export async function rejectReservation(id: string, reason?: string, shouldSendEmail = true) {
  return api<{ reservation: ReservationDetail; emailSent: boolean; emailError: string | null }>(
    `/api/admin/reservations/${id}/reject`,
    { method: "POST", body: JSON.stringify({ reason, sendEmail: shouldSendEmail }) }
  );
}

export async function cancelReservation(id: string, reason?: string) {
  return api<{ reservation: ReservationDetail }>(`/api/admin/reservations/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function completeReservation(id: string) {
  return api<{
    reservation: ReservationDetail;
    documentNumber: string | null;
    documentType: "INVOICE" | "CASH_RECEIPT" | null;
    documentError: string | null;
    emailSent: boolean;
    emailError: string | null;
  }>(`/api/admin/reservations/${id}/complete`, { method: "POST" });
}

// ─── Dashboard ────────────────────────────────────────────

export type DashboardData = {
  pendingCount: number;
  upcomingThisWeek: number;
  thisMonth: {
    count: number;
    distanceKm: number;
    revenueCzk: number;
  };
  nextReservations: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    status: ReservationStatus;
    snapshotName: string;
    snapshotCity: string;
    childrenCount: number;
    totalPriceCzk: number;
  }[];
};

export async function getDashboard() {
  const res = await api<DashboardData>("/api/admin/dashboard");
  return res;
}

// ─── Faktury ──────────────────────────────────────────────

export type InvoiceListItem = {
  id: string;
  number: string;
  variableSymbol: string;
  dateIssued: string;
  dateDue: string;
  totalCzk: number;
  paid: boolean;
  paidAt: string | null;
  pdfPath: string | null;
  sentAt: string | null;
  reservation: {
    id: string;
    snapshotName: string;
    date: string;
    status: ReservationStatus;
  };
};

export type InvoiceOnReservation = {
  id: string;
  number: string;
  variableSymbol: string;
  dateIssued: string;
  dateDue: string;
  totalCzk: number;
  paid: boolean;
  paidAt: string | null;
  pdfPath: string | null;
  sentAt: string | null;
};

export async function listInvoices(paid?: boolean) {
  const q = paid !== undefined ? `?paid=${paid}` : "";
  const res = await api<{ invoices: InvoiceListItem[] }>(`/api/admin/invoices${q}`);
  return res.invoices;
}

export function invoicePdfUrl(id: string): string {
  // Pro browserové otevření bez fetch — potřebujeme token v headeru,
  // ale otevírat v novém tabu. Workaround: backend akceptuje i cookie.
  return `/api/admin/invoices/${id}/pdf`;
}

export async function markInvoicePaid(id: string, paid: boolean, paymentNote?: string) {
  return api(`/api/admin/invoices/${id}/mark-paid`, {
    method: "POST",
    body: JSON.stringify({ paid, paymentNote }),
  });
}

export async function resendInvoice(id: string) {
  return api(`/api/admin/invoices/${id}/resend`, { method: "POST" });
}

export async function regenerateInvoice(id: string) {
  return api(`/api/admin/invoices/${id}/regenerate`, { method: "POST" });
}

/**
 * Stáhne PDF fakturu přes fetch (s Authorization tokenem) a otevře/stáhne v prohlížeči.
 * Používat místo window.open(invoicePdfUrl) kvůli autorizaci.
 */
export async function openInvoicePdf(id: string) {
  const token = localStorage.getItem("admin_token");
  const res = await fetch(`/api/admin/invoices/${id}/pdf`, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`PDF se nepodařilo načíst (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ─── Školky (databáze zákazníků) ──────────────────────────

export type Kindergarten = {
  id: string;
  name: string;
  ico: string | null;
  street: string;
  city: string;
  zip: string;
  country: string;
  lat: number | null;
  lon: number | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  notes: string | null;
  createdAt: string;
  _count?: { reservations: number };
};

export type KindergartenDetail = Kindergarten & {
  lifetimeSpent: number;
  visitCount: number;
  reservations: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    status: ReservationStatus;
    childrenCount: number;
    totalPriceCzk: number;
    paymentMethod: "BANK_TRANSFER" | "CASH";
  }[];
};

export async function listKindergartens(search?: string) {
  const q = search ? `?search=${encodeURIComponent(search)}` : "";
  const res = await api<{ kindergartens: Kindergarten[] }>(`/api/admin/kindergartens${q}`);
  return res.kindergartens;
}

export async function getKindergarten(id: string) {
  const res = await api<{ kindergarten: KindergartenDetail }>(`/api/admin/kindergartens/${id}`);
  return res.kindergarten;
}

export type KindergartenInput = Omit<
  Kindergarten,
  "id" | "createdAt" | "_count"
>;

export async function createKindergarten(data: KindergartenInput) {
  const res = await api<{ kindergarten: Kindergarten; linkedReservations: number }>(
    "/api/admin/kindergartens",
    { method: "POST", body: JSON.stringify(data) }
  );
  return res;
}

export async function updateKindergarten(id: string, data: Partial<KindergartenInput>) {
  const res = await api<{ kindergarten: Kindergarten }>(`/api/admin/kindergartens/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return res.kindergarten;
}

export async function deleteKindergarten(id: string) {
  return api(`/api/admin/kindergartens/${id}`, { method: "DELETE" });
}

export async function kindergartenFromReservation(reservationId: string) {
  const res = await api<{ kindergarten: Kindergarten }>(
    `/api/admin/kindergartens/from-reservation/${reservationId}`,
    { method: "POST" }
  );
  return res.kindergarten;
}

// ─── Site images (hero + galerie) ────────────────────────

export type SiteImage = {
  id: string;
  url: string;
  alt: string | null;
  caption: string | null;
  category: string | null;
  sortOrder: number;
  active: boolean;
  createdAt: string;
};

export async function listSiteImages(category?: string) {
  const q = category ? `?category=${encodeURIComponent(category)}` : "";
  const res = await api<{ images: SiteImage[] }>(`/api/admin/site-images${q}`);
  return res.images;
}

export async function uploadSiteImage(
  file: File,
  meta: { alt?: string; caption?: string; category?: string; sortOrder?: number }
): Promise<SiteImage> {
  const form = new FormData();
  form.append("image", file);
  if (meta.alt) form.append("alt", meta.alt);
  if (meta.caption) form.append("caption", meta.caption);
  if (meta.category) form.append("category", meta.category);
  if (meta.sortOrder != null) form.append("sortOrder", String(meta.sortOrder));

  const token = localStorage.getItem("admin_token");
  const res = await fetch("/api/admin/site-images", {
    method: "POST",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((payload as any)?.message ?? `Upload selhal (${res.status})`);
  return (payload as any).image;
}

export async function updateSiteImage(
  id: string,
  data: Partial<{
    alt: string | null;
    caption: string | null;
    category: string;
    sortOrder: number;
    active: boolean;
  }>
) {
  const res = await api<{ image: SiteImage }>(`/api/admin/site-images/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return res.image;
}

export async function deleteSiteImage(id: string) {
  return api(`/api/admin/site-images/${id}`, { method: "DELETE" });
}

// ─── Program gallery (pro existující Program) ────────────

export type ProgramGalleryImage = {
  id: string;
  programId: string;
  url: string;
  alt: string | null;
  sortOrder: number;
};

export async function addProgramGalleryImage(programId: string, file: File, alt?: string) {
  const form = new FormData();
  form.append("image", file);
  if (alt) form.append("alt", alt);
  const token = localStorage.getItem("admin_token");
  const res = await fetch(`/api/admin/programs/${programId}/images`, {
    method: "POST",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((payload as any)?.message ?? `Upload selhal (${res.status})`);
  return (payload as any).image as ProgramGalleryImage;
}

export async function deleteProgramGalleryImage(programId: string, imageId: string) {
  return api(`/api/admin/programs/${programId}/images/${imageId}`, { method: "DELETE" });
}

export async function uploadProgramImage(file: File): Promise<string> {
  const form = new FormData();
  form.append("image", file);

  const token = localStorage.getItem("admin_token");
  const res = await fetch("/api/admin/uploads/program-image", {
    method: "POST",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((payload as any)?.message ?? `Upload selhal (${res.status})`);
  return (payload as any).url as string;
}
